import { forwardRef, useState, useRef, useCallback } from 'react';
import type { RefObject, MouseEvent, KeyboardEvent } from 'react';
import styled, { css } from 'styled-components';

import { MenuButton, registerIcon, defaultThemeProp, useTheme } from '@pega/cosmos-react-core';
import * as moreIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/more.icon';

import useCloseOnEditorClick from './hooks/useCloseOnEditorClick';

registerIcon(moreIcon);

export interface CellPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TableCellMenuProps {
  position: CellPosition | null;
  containerRef: RefObject<HTMLElement>;
  onAddRowBelow: () => void;
  onAddRowAbove: () => void;
  onAddColumnAfter: () => void;
  onAddColumnBefore: () => void;
  onDeleteRow: () => void;
  onDeleteColumn: () => void;
  onDeleteTable: () => void;
  onGoToNextCell?: () => void;
  onReturnToCell?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  labels: {
    tableOptions: string;
    addRowBelow: string;
    addRowAbove: string;
    addColumnAfter: string;
    addColumnBefore: string;
    deleteRow: string;
    deleteColumn: string;
    deleteTable: string;
  };
}

const MENU_BUTTON_SPACING_MULTIPLIER = 3.5;

const StyledTableCellMenuButton = styled(MenuButton)<{
  $visible: boolean;
  $top: number;
  $left: number;
}>(
  ({ theme, $visible, $top, $left }) => css`
    position: absolute;
    display: ${$visible ? 'inline-flex' : 'none'};
    z-index: ${theme.base['z-index'].popover};
    top: ${$top}rem;
    left: ${$left}rem;
    transform: translateY(-50%);

    &&& {
      min-width: 1.5rem;
      min-height: 1.5rem;
      padding: 0;
    }

    &&&:hover,
    &&&:focus {
      box-shadow: none;
      background-color: ${theme.base.palette['primary-background']};
    }
  `
);

StyledTableCellMenuButton.defaultProps = defaultThemeProp;

const TableCellMenu = forwardRef<HTMLButtonElement, TableCellMenuProps>(function TableCellMenu(
  {
    position,
    containerRef,
    onAddRowBelow,
    onAddRowAbove,
    onAddColumnAfter,
    onAddColumnBefore,
    onDeleteRow,
    onDeleteColumn,
    onDeleteTable,
    onGoToNextCell,
    onReturnToCell,
    onMouseEnter,
    onMouseLeave,
    labels
  },
  ref
) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const theme = useTheme();

  const handleEditorClick = useCallback(() => {
    buttonRef.current?.blur();
    setIsMenuOpen(false);
  }, []);
  useCloseOnEditorClick(isMenuOpen, handleEditorClick);

  if (!position || !containerRef.current) {
    return null;
  }

  const containerRect = containerRef.current.getBoundingClientRect();
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const baseSpacing = parseFloat(theme.base.spacing) || 1;
  const buttonWidthRem = MENU_BUTTON_SPACING_MULTIPLIER * baseSpacing;

  const top = (position.top - containerRect.top + position.height / 2) / rootFontSize;
  const left =
    (position.left - containerRect.left + position.width) / rootFontSize - buttonWidthRem;

  const handleButtonMouseDown = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleButtonClick = () => {
    setIsMenuOpen(prev => !prev);
  };

  const handleButtonKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!isMenuOpen) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        onGoToNextCell?.();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onReturnToCell?.();
      }
    }
  };

  const setRefs = (el: HTMLButtonElement | null) => {
    buttonRef.current = el;
    if (typeof ref === 'function') {
      ref(el);
    } else if (ref) {
      ref.current = el;
    }
  };

  const handleMenuItemClick = (id: string) => {
    setIsMenuOpen(false);
    switch (id) {
      case 'add-row-below':
        onAddRowBelow();
        break;
      case 'add-row-above':
        onAddRowAbove();
        break;
      case 'add-column-after':
        onAddColumnAfter();
        break;
      case 'add-column-before':
        onAddColumnBefore();
        break;
      case 'delete-row':
        onDeleteRow();
        break;
      case 'delete-column':
        onDeleteColumn();
        break;
      case 'delete-table':
        onDeleteTable();
        break;
      default:
        break;
    }
  };

  // Key based on position forces remount when cell changes, closing any open menu
  const menuKey = `${position.top}-${position.left}`;

  return (
    <StyledTableCellMenuButton
      key={menuKey}
      ref={setRefs}
      $visible={!!position}
      $top={top}
      $left={left}
      text={labels.tableOptions}
      variant='simple'
      icon='more'
      iconOnly
      onMouseDown={handleButtonMouseDown}
      onClick={handleButtonClick}
      onKeyDown={handleButtonKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      popover={{ placement: 'bottom-end' }}
      menu={{
        items: [
          { id: 'add-row-below', primary: labels.addRowBelow },
          { id: 'add-row-above', primary: labels.addRowAbove },
          { id: 'add-column-after', primary: labels.addColumnAfter },
          { id: 'add-column-before', primary: labels.addColumnBefore },
          { id: 'delete-row', primary: labels.deleteRow },
          { id: 'delete-column', primary: labels.deleteColumn },
          { id: 'delete-table', primary: labels.deleteTable }
        ],
        onItemClick: handleMenuItemClick
      }}
    />
  );
});

export default TableCellMenu;
